import { useEffect, useRef, useState } from "react";
import { Button } from "@legends/ui";
import { verifyEmail, type AuthUser } from "./api";

export interface VerifyEmailScreenProps {
  /** The one-time token from the emailed link (?token=…). */
  token: string;
  /** Called on success — the fan is now verified AND signed in (the API set the
   *  session cookie); App adopts the authenticated status. */
  onVerified: (user: AuthUser) => void;
  /** Return to the sign-in screen (shown on an invalid/expired link). */
  onBackToLogin: () => void;
}

// Landing page for the verification link (LSC-554 / ADR 0233) at /verify-email?token=…
// (resolved before AuthGate). Confirms the token once on mount; on success the fan is
// signed in and dropped into the app, on failure they get a clear retry path.
export default function VerifyEmailScreen({ token, onVerified, onBackToLogin }: VerifyEmailScreenProps) {
  const [state, setState] = useState<"verifying" | "ok" | "error">("verifying");
  const [message, setMessage] = useState("");
  const ran = useRef(false); // StrictMode double-mount guard — consume the token once

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const user = await verifyEmail(token);
        if (cancelled) return;
        setState("ok");
        onVerified(user);
      } catch (err: unknown) {
        if (cancelled) return;
        setState("error");
        setMessage(err instanceof Error ? err.message : "This verification link is invalid or has expired.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, onVerified]);

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: 360,
          display: "grid",
          gap: 14,
        }}
      >
        {state === "verifying" && (
          <>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)" }}>Verifying…</h1>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>Confirming your email address.</p>
          </>
        )}
        {state === "ok" && (
          <>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)" }}>You're in</h1>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>
              Your email is verified and you're signed in.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)" }}>Link expired</h1>
            <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)", lineHeight: 1.6 }}>
              {message || "This verification link is invalid or has expired."}
            </p>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)", lineHeight: 1.6 }}>
              Sign in to get a fresh verification email.
            </p>
            <Button variant="accent" full onClick={onBackToLogin}>
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
